/**
 * Dollar figures, and money in the currency it is actually stated in.
 *
 * Two compact styles are in use and both are named here rather than picked
 * between, because each is what its screens have always printed:
 * - `formatCompactUsd`: `Intl` compact, up to N places, trailing zeros
 *   dropped ("$3.1M", "$12"), except that an amount to the cent keeps both
 *   cents ("$0.70"). The investor screens.
 * - `formatFixedCompactUsd`: fixed places per step, kept ("$2.11M", "$5.3K",
 *   "$12.00"). The strategist dashboard.
 * A dollar sign is printed only on a figure the caller has established as
 * dollars.
 */
let compactUsdCache: Map<string, Intl.NumberFormat> | undefined;

function compactUsdFormatter(
  maximumFractionDigits: number,
  minimumFractionDigits: number
): Intl.NumberFormat {
  const key = `${minimumFractionDigits}:${maximumFractionDigits}`;
  compactUsdCache ??= new Map();
  let formatter = compactUsdCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      // ICU defaults differ across Node releases. Setting this explicitly
      // prevents Node 22 from retaining a trailing zero (`$3.10M`) while
      // newer runtimes render `$3.1M` for the same value.
      minimumFractionDigits,
      maximumFractionDigits
    });
    compactUsdCache.set(key, formatter);
  }
  return formatter;
}

export interface CompactUsdOptions {
  /**
   * Default 1. At 2 or more, a figure below a thousand is stated in dollars
   * and cents: both cents or none ("$0.70", "$704"), never one ("$0.7").
   */
  maximumFractionDigits?: number;
  /** Default 0: trailing zeros dropped. 2 keeps cents ("$3.10M"). */
  minimumFractionDigits?: number;
}

/**
 * The figure as a compact dollar string at `digits`, with the cents rule
 * above. It builds its own formatter from `digits`, so the places a caller
 * states are the places applied: up to two
 * places drops a trailing zero, which on a compact step is the style
 * ("$1.2M") and on an amount in dollars and cents reads as a typo ("$0.7",
 * "$715.5"). An unscaled figure that lands on a single cent digit is
 * stated again with both. A compact step and a whole amount are untouched.
 */
export function formatWithCents(
  value: number | `${number}`,
  { maximumFractionDigits, minimumFractionDigits }: Required<CompactUsdOptions>
): string {
  const formatter = compactUsdFormatter(
    maximumFractionDigits,
    minimumFractionDigits
  );
  if (maximumFractionDigits < 2 || minimumFractionDigits >= 2) {
    return formatter.format(value);
  }
  const parts = formatter.formatToParts(value);
  const fraction = parts.find((part) => part.type === 'fraction')?.value;
  const scaled = parts.some((part) => part.type === 'compact');
  if (scaled || fraction == null || fraction.length >= 2) {
    return parts.map((part) => part.value).join('');
  }
  return compactUsdFormatter(maximumFractionDigits, 2).format(value);
}

/** A number as a compact dollar figure: "$3.1M". */
export function formatCompactUsd(
  value: number,
  {
    maximumFractionDigits = 1,
    minimumFractionDigits = 0
  }: CompactUsdOptions = {}
): string {
  return formatWithCents(value, {
    maximumFractionDigits,
    minimumFractionDigits
  });
}
